import CryptoJS from "crypto-js";
import config, { S3_GYL_ICONS } from "./config";
import { ACTIVE_MERCHANT, BORDERLESS, COMPLIANCE_FAILURE, COMPLIANCE_IN_PROGRESS, COMPLIANCE_INCOMPLETE, FIAT_CONFIG_STATUS, fiatVendors, LOCALSTORAGE_LANGUAGE_KEY, paymentOptions, transferType } from "./constants";
import * as Sentry from "@sentry/browser";
import { languages } from "../locale";
import store from "../store";
import { getConfigurationsSelector, getUserSelector } from "./selectorHelpers";

var WAValidator = require("wallet-address-validator/dist/wallet-address-validator");

export const Decrypt = (data) => {
  const decrypted = CryptoJS.AES.decrypt(data, config.SECRET_KEY).toString(
    CryptoJS.enc.Utf8
  );
  return decrypted;
};

export const Encrypt = (data) => {
  const stringData = JSON.stringify(data);
  const encrypted = CryptoJS.AES.encrypt(
    stringData,
    config.SECRET_KEY
  ).toString();
  return encrypted;
};
export const renderIfElse = (condition, component1, component2) => {
  if (condition) {
    return component1;
  } else {
    return component2;
  }
};

export const formatNumber = (number) => {
  if (!number || number < 0 || isNaN(number)) {
    return "0.00";
  }
  const cleaned = String(number).replace(/,/g, "").trim();
  const [integerPart = "0", decimalPart = ""] = cleaned.split(".");
  const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  const formattedDecimal = decimalPart.length < 2
    ? decimalPart.padEnd(2, "0")
    : decimalPart;
  return `${formattedInteger}.${formattedDecimal}`;
};

export const formatCryptoDecimals = (number) => {
  const value = number?.toString() || "0";
  const decimals = value.split(".")[1];
  if (decimals && decimals !== undefined && decimals.length > 6) {
    const decimalValue = parseFloat(value).toFixed(6);
    return decimalValue;
  }
  return value;
};
/**
 *
 * @param {<merchant object>} merchant
 * @returns string
 */
export const getMerchantName = (merchant) => {
  if (merchant.shopData && merchant.shopData.companyName) {
    return merchant.shopData.companyName;
  } else return merchant.fullname;
};

export const cleanCache = async (version) => {
  try {
    // to handle the devices that dont support force cache clean
    if ('caches' in window) {
      const cacheNames = await caches.keys();
      await Promise.all(cacheNames.map((name) => caches.delete(name)));
    }

    clearLocalStorage();

    if (version) {
      localStorage.setItem("version", version);
    }

    window.location.reload(true);
  } catch (err) {
    // generate sentry log for clean failed to track the device enviornment
    console.error("Cache cleaning failed:", err);
    window.location.href = window.location.pathname + "?v=" + new Date().getTime();
  }
};


export const validator = (text, cryptoCurrency, network) => {
  try {
    if (['SOL', 'MATIC', 'POL'].includes(cryptoCurrency)) return true;
    if (cryptoCurrency === 'USDT' && network === 'tron') return true;
    return WAValidator.validate(text, cryptoCurrency);
  } catch (err) {
    if (["USDC", "USDT"].indexOf(cryptoCurrency) > -1) {
      return WAValidator.validate(text, "ETH");
    }
    return false;
  }
};

export const isBlockedUser = (user, status) => {
  // eslint-disable-next-line eqeqeq
  return user.kycStatus == 3 || user.kycStatus == 5;
};

export const isFiatTransaction = (data) => {
  return transferType.fiat.includes(data.service) && !data?.cryptoCurrency?.id;
}

export const isTotalFeeAvailable = (data) => {
  return !!data?.feeCharged?.totalDeductedFee && data?.service === BORDERLESS;
}

export const appVersionCheck = async () => {
  try {
    const appVersion = localStorage.getItem('app-version');

    // fech version.json from the public folder
    const response = await fetch('/version.json', { cache: 'no-store' });
    if (!response.ok) throw new Error('Failed to fetch version.json');

    const { version: currentAppVersion } = await response.json();

    if (!appVersion) {
      localStorage.setItem('app-version', currentAppVersion);
    } else if (appVersion !== currentAppVersion) {
      cleanCache();
      clearLocalStorage();
      localStorage.setItem('app-version', currentAppVersion);
      window.location.reload(true); // Force reload to get new assets
    }
  } catch (err) {
    // sentry logging
    console.error('App version check failed:', err.message);
  }
};
export const clearLocalStorage = () => {
  let rememberMeKeys = [
    "email_payee",
    "version",
    "walletaddress-ETH",
    "walletaddress-USDT",
    "walletaddress-USDC",
    "walletaddress-BTC",
    "new_user_email",
    "new_user",
    "app-version",
    "mfa-pop",
    "getting-started",
    "app-version",
    "statement-pop",
    ACTIVE_MERCHANT
  ];
  let localStorageData = {};
  for (let i = 0; i < localStorage.length; i++) {
    localStorageData[localStorage.key(i)] = localStorage.getItem(
      localStorage.key(i)
    );
  }

  localStorage.clear();

  for (let rememberMeKey of rememberMeKeys) {
    if (localStorageData[rememberMeKey]) {
      localStorage.setItem(rememberMeKey, localStorageData[rememberMeKey]);
    }
  }
  return;
}


export const trimText = (text, maxLength) => {
  if (text.length > maxLength) {
    return text.substring(0, maxLength) + "...";
  }
  return text;
}

export const removeDynamicPart = (path, payment) => {

  // Split the path by '/'
  const parts = path.split('/');

  // Find the index of the dynamic part (e.g., UUID)
  const uuidIndex = parts.findIndex(part => part.length > 5 && part.match(/^[0-9a-f]{8}-(?:[0-9a-f]{4}-){3}[0-9a-f]{12}$/));
  // Remove the dynamic part from the path
  let pathWithoutUuid = path;
  if (uuidIndex > 0) {
    pathWithoutUuid = parts.slice(0, uuidIndex).join('/');
  }
  if (pathWithoutUuid.includes('invite')) {
    const regexPattern = /\/[^/]+$/;
    return pathWithoutUuid?.replace(regexPattern, '')
  }

  if (fiatVendors.indexOf(payment) >= 0 && path.includes('withdraw') && parts[parts.length - 1] > 1) {
    return pathWithoutUuid + `/${paymentOptions.pockyt}`
  }

  return pathWithoutUuid;
}

export const removeNonCryptoHelp = (data, enabled) => {
  let values = data;
  if (!enabled && data) {
    values = data.filter(val => !val.removeable)
  }
  return values;
}

export const extractEthereumAddress = (inputString) => {
  // Regular expression to match Ethereum addresses
  const regex = /(0x[a-fA-F0-9]{40})/;

  // Use match to find the first occurrence of the address in the string
  const match = inputString.match(regex);

  // If a match is found, return the Ethereum address, otherwise return null
  return match ? match[0] : null;
}

/**
 * Builds a RegExp from bank-form API regex strings.
 * Handles: plain patterns, /pattern/flags literals, and Unicode property escapes (\p{}/\P{}) which require the u flag.
 * Returns null if the pattern is invalid so callers can skip matches validation.
 */
export const toValidationRegExp = (input) => {
  if (!input || typeof input !== "string") return null;

  let pattern = input;
  let flags = "";

  const literal = input.match(/^\/([\s\S]*)\/([gimsuy]*)$/);
  if (literal) {
    pattern = literal[1];
    flags = literal[2] || "";
  }

  if (/\\[pP]\{/.test(pattern) && !flags.includes("u")) {
    flags += "u";
  }

  try {
    return new RegExp(pattern, flags);
  } catch {
    return null;
  }
};

export const initalizeUserForLoggers = async (data) => {
  try {
    Sentry.setUser({ email: data.email });
  } catch (err) {
    console.log(err.message);
  }
}
export const persistLanguagePreference = (language) => {
  localStorage.setItem(LOCALSTORAGE_LANGUAGE_KEY, language);
}

export const retrieveLanguagePreference = () => {
  try {
    const language = localStorage.getItem(LOCALSTORAGE_LANGUAGE_KEY);

    //validate the language
    const languageKeys = Object.keys(languages);
    if (language && languageKeys.includes(language)) {
      return language;
    } else {
      return languages.EN.id;
    }
  } catch (error) {
    return languages.EN.id;
  }

}


export const checkPockytEnabled = (fiatService) => {
  return fiatVendors.includes(fiatService?.serviceName);
}

export const getCurrentTitles = (
  active,
  payment,
  t,
  bankFlag,
  optionSelectMode = "country-currency",
) => {
  switch (active) {
    case 0:
      return `${t("selectAmount")}`;
    case 1:
      return `${t("selectPayment")}`;
    case 2:
      if (payment === paymentOptions.crypto)
        return `${t("selectCurrency")}`;
      if (payment === paymentOptions.crypto2)
        return `${t("Summary")}`;
      if (payment === paymentOptions.crypto3)
        return `${t("Transfer options")}`;
      else if (fiatVendors.includes(payment)) {
        if (bankFlag) {
          if (optionSelectMode === "transfer-method") {
            return t("currencyOptions");
          }
          return t("pockyt.step1");
        }
        return t("bankAccount")
      }
      break;
    case 3:
      if (payment === paymentOptions.crypto)
        return `${t("addWallet")}`;
      if (payment === paymentOptions.crypto3)
        return `${t("Summary")}`;
      else if (fiatVendors.includes(payment)) {
        if (bankFlag) {
          return t("addBankAccount");
        }
        return t("Summary")
      }
      break;
    case 4:
      if (payment === paymentOptions.crypto) return;
      else if (fiatVendors.includes(payment)) {
        if (bankFlag) {
          return t("Summary");
        } else {
          return t("Summary")
        }
      }
      break;
    case 5:
      if (payment === paymentOptions.crypto) return;
      break;
    case 6:
      if (payment === paymentOptions.crypto) return;
      break;
    case 7:
      if (payment === paymentOptions.crypto) return;
      break
    default:
      return "";
  }
};

export const getBackTitles = (active, skip, payment, t, bankFlag) => {
  const constant = active;
  if (skip.includes(active)) {
    active = active - 1;
  }
  if (skip.includes(active) && constant === 4) {
    active = active - 1;
  }
  switch (active) {
    case 0:
      return `${t("selectAmountBack")}`;
    case 1:
      return `${t("selectPaymentBack")}`;
    case 2:
      if (payment === paymentOptions.crypto)
        return `${t("selectCurrencyBack")}`;
      if (payment === paymentOptions.crypto3)
        return t("Transfer options");
      else if (fiatVendors.includes(payment)) {
        if (bankFlag) {
          return t("pockyt.step1");
        }
        return t("bankAccount")
      }
      break;
    case 3:
      if (payment === paymentOptions.crypto)
        return `${t("addWalletBack")}`;
      else if (fiatVendors.includes(payment)) {
        if (bankFlag) {
          return t("addBankAccount");
        }
        return t("bankAccount")
      }
      break;
    case 4:
      if (payment === paymentOptions.crypto) return;
      else if (fiatVendors.includes(payment)) {
        if (bankFlag) {
          return t("addbankdetails");
        } else {
          return t("addbankdetails");
        }
      }
      break;
    case 5:
      if (payment === paymentOptions.crypto) return;
      break;
    case 6:
      if (payment === paymentOptions.crypto) return;
      break;
    case 7:
      if (payment === paymentOptions.crypto) return;
      break;
    default:
      return "";
  }
};

export const getFiatIcon = (icon) => {
  try {
    const Logo = `${S3_GYL_ICONS}/fiat/${icon.toLowerCase()}.svg`;
    return Logo;
  } catch (err) {
    return "";
  }
};
export const isMaskedEmail = (input) => {
  const emailReg = /^[a-zA-Z0-9._%+\-*]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  return emailReg.test(input || '')
}
export const maskDigits = (input) => {
  if (!input) return '';
  // Convert input to a string to handle both string and number inputs
  const emailReg = /^[a-zA-Z0-9._%+\-*]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  if (emailReg.test(input)) {
    return input;
  }
  const digits = input.toString();

  // Get the last 4 digits
  const lastFour = digits.slice(-4);

  // Create the masked part with 3 * characters followed by the last 4 digits
  const masked = '***' + (lastFour || '');

  return masked;
}

export const isKycVerified = (user) => {
  if (!user) {
    return false
  }
  if (user.meta && user.meta?.isExternalPayee && user.meta?.disableComplianceReview) {
    return true
  }
  return Number(user.kycStatus) === 2
}

export const replaceLabels = (label) => {
  // 'Purpose of Bank Account'
  if (label === 'Purpose of Bank Account') {
    return 'Bank Account Type';
  }
  return label;
}
export const showVerificationPage = (user) => {
  if (!user) {
    return false;
  }
  const { kycMismatch, kycStatus } = user;
  const status = Number(kycStatus);
  if (kycMismatch || status === 4 || status === 5 || status === 1 || status === 2) {
    return false;
  }
  return true;
}

export const doesBankRequireVerification = (type) => {
  const verificationRequired = [paymentOptions.pockyt];
  return verificationRequired.indexOf(type) > -1;
}
export const parseErrorMessage = (message) => {
  // replace
  message = message.replace('details.bankRoutingNumber', 'Bank Routing Number');
  message = message.replace('details.swiftOrBicNumber', 'Swift or Bic Number');
  return message;
}

export const filterCurrencies = (currencies, allowedCurrencies) => {
  if (!Array.isArray(currencies) || currencies.length === 0) return [];

  return currencies.filter(currency =>
    allowedCurrencies.some(allowed =>
      currency.currency === allowed.currency && currency.network === allowed.network
    )
  );
};

export const filterOutCurrencies = (currencies, removedCurrencies) => {
  if (!Array.isArray(currencies) || currencies.length === 0) return [];

  return currencies.filter(currency =>
    !removedCurrencies.some(allowed =>
      currency.currency === allowed.currency && currency.network === allowed.network
    )
  );
};

export const getSymbol = (state) => state?.dashboard?.currencies;
export const findSymbol = (currencies, account, currency) => {
  if (account && account?.payoutAccount?.configuration?.currency)
    return currencies?.find(c => c.id.toUpperCase() === account?.payoutAccount?.configuration?.currency.toUpperCase())?.symbol;
  if (currency) {
    return currencies?.find(c => c.id.toUpperCase() === currency.toUpperCase())?.symbol;
  }
  return '$'
}
export const findCurrency = (account) => account?.payoutAccount?.configuration?.currency;
export const isFunction = (callback) => typeof callback === 'function';
export const showComplianceLink = (compliance, fiatService) => {
  return [COMPLIANCE_IN_PROGRESS, COMPLIANCE_INCOMPLETE, COMPLIANCE_FAILURE].indexOf(compliance?.status) >= 0 && fiatService?.serviceName === paymentOptions.bd;
}

export const getFiatErrorTitle = (fiatStatus) => {
  switch (fiatStatus?.errorType) {
    case FIAT_CONFIG_STATUS.default:
      return 0;
    case FIAT_CONFIG_STATUS.warn:
      return 'warning';
    case FIAT_CONFIG_STATUS.error:
      return 'error';
    case FIAT_CONFIG_STATUS.info:
      return 'info';
    default:
      return 0;
  }
}

export const getFiatErrorStyle = (fiatStatus) => {
  switch (fiatStatus?.errorType) {
    case FIAT_CONFIG_STATUS.default:
      return {
        color: '#F8F8F8'
      };
    case FIAT_CONFIG_STATUS.warn:
      return {
        color: '#FF9800'
      }
    case FIAT_CONFIG_STATUS.error:
      return {
        color: 'rgb(227, 114, 114)'
      }
    case FIAT_CONFIG_STATUS.info:
      return {
        color: '#2196F3'
      }
    default:
      return 0;
  }
}

export const showFiatPopup = (fiatStatus) => {
  return [FIAT_CONFIG_STATUS.error, FIAT_CONFIG_STATUS.warn, FIAT_CONFIG_STATUS.info].indexOf(fiatStatus?.errorType) > -1;
}

export const getCommonConsts = (text) => {
  switch (text) {
    case 'same_next_day':
      return 'Same Day ACH';
    case 'next_day':
      return 'Next Day ACH';
    case 'instant_transfer':
      return 'Instant Transfer';
    case 'paypal':
      return 'PayPal';
    case 'venmo':
      return 'Venmo';
    case 'bank_wire':
      return 'Bank Wire';
    case 'BANK_DEPOSIT':
      return 'BANK DEPOSIT';
    default:
      return text?.toUpperCase();
  }
}

export const getCustomErrorMessage = (customErrors, data) => {
  if (customErrors?.country && data?.name === 'countryCode') {
    return customErrors?.country;
  }
  if (customErrors?.state && data?.name === 'state') {
    return customErrors?.state;
  }
  if (customErrors?.city && data?.name === 'city') {
    return customErrors?.city;
  }
  if (customErrors?.street1 && data?.name === 'street') {
    return customErrors?.street1;
  }
  return '';
}

export const isMfaEnforced = () => {
  const state = store.getState();
  const config = getConfigurationsSelector(state);
  const user = getUserSelector(state);
  return (config?.enforceMfa || false) && !user?.isMfaEnabled;
}
export function formatFee(option, account) {
  const currency = findCurrency(account);
  if (option.percentage || option.fiat) {
    const fiatPart = option.fiat ? `${option.fiat} ${currency} ${option.percentage ? ' + ' : ''}` : '';
    const percentagePart = option.percentage ? `${option.percentage}%` : '';
    return `${fiatPart}${percentagePart}`.trim();
  }
  return '';
}

export const getDisplayFee = (option, account) => {
  const currency = findCurrency(account);

  const hasPercent = option.displayFeePercent !== null && option.displayFeePercent !== undefined;
  const hasFiat = option.displayFeeFiat;

  if (hasPercent || hasFiat) {
    const fiatPart = hasFiat ? `${option.displayFeeFiat} ${currency}${hasPercent ? ' + ' : ''}` : '';
    const percentagePart = hasPercent ? `${option.displayFeePercent}%` : '';
    return `${fiatPart}${percentagePart}`.trim();
  }

  return '';
};

export function formatProcessingFee(feestructure, account) {
  const currency = findCurrency(account);
  const flatFee = feestructure?.localProcessingFeeFlat;
  const percentFee = parseFloat(feestructure?.processingFeePercent?.toString() || "0");

  let result = "";

  if (flatFee) {
    result += `${flatFee} ${currency}`;
  }

  if (percentFee !== 0) {
    result += `${flatFee ? ' + ' : ''}${percentFee}%`;
  }

  if (!flatFee && percentFee === 0) {
    result = `0 ${currency}`;
  }

  return result.trim();
}
